/**
 * スーパーリロード処理を管理するヘルパー関数群
 * 1日に一度、自動的にYakurekiデータのリセット処理を実行する
 */

import {
  fetchAllYakurekiPages,
  type FetchAllYakurekiPagesOptions,
} from '@/shared/apis/yakurekiPagination'

// ローカルストレージのキー
const SUPER_RELOAD_TIMESTAMP_KEY = 'super_reload_last_executed'

/**
 * スーパーリロード処理の実行
 * localStorageをクリアして全件取得し、実行時刻を記録する
 *
 * 差分同期 (delta) は削除を運べないため、一覧から消えた行を端末に伝える手段はこの全件
 * 取り直しだけ。日次の自動実行のほか、録音の終端 (停止 / 破棄) でも呼ぶ (useRecordingStatus)。
 * 書込の直後に呼ぶときは `forceRefresh` で back の 10 秒キャッシュを迂回する
 * (yakurekiPagination.ts の FetchAllYakurekiPagesOptions)。
 */
export const executeSuperReload = async (
  options: FetchAllYakurekiPagesOptions = {}
) => {
  // localStorageをクリア
  localStorage.removeItem('yakureki_last_updated')
  localStorage.removeItem('yakureki_data')

  // 全件取得（ページネーション対応）
  const results = await fetchAllYakurekiPages(options)
  const now = new Date().toISOString()

  // 新しいデータとタイムスタンプを保存
  localStorage.setItem('yakureki_last_updated', now)
  localStorage.setItem('yakureki_data', JSON.stringify(results))
  localStorage.setItem(SUPER_RELOAD_TIMESTAMP_KEY, now)

  return results
}

/**
 * 最後にスーパーリロードが実行された時刻を取得
 * @returns 最後の実行時刻のDateオブジェクト、未実行の場合はnull
 */
export const getLastSuperReloadTime = (): Date | null => {
  const timestamp = localStorage.getItem(SUPER_RELOAD_TIMESTAMP_KEY)
  return timestamp ? new Date(timestamp) : null
}

/**
 * 1日が経過したかどうかをチェック
 * @returns 1日以上経過している場合はtrue
 */
export const shouldExecuteSuperReload = (): boolean => {
  const lastExecuted = getLastSuperReloadTime()

  // 実行履歴がない場合は実行すべき
  if (!lastExecuted) {
    return true
  }

  const now = new Date()
  const oneDayInMs = 24 * 60 * 60 * 1000 // 24時間をミリ秒で表現
  const timeDifference = now.getTime() - lastExecuted.getTime()

  return timeDifference >= oneDayInMs
}

/**
 * 条件をチェックして必要に応じてスーパーリロードを実行
 * @returns 実行した場合はYakurekiの配列、実行しなかった場合はnull
 */
export const checkAndExecuteSuperReload = async () => {
  if (shouldExecuteSuperReload()) {
    return await executeSuperReload()
  }

  return null
}
